import { COUNTRY_LABELS, SELECTABLE_COUNTRIES, countryOptions, type Country } from '@acs/shared';
import { Field, Select } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';

/**
 * The country picker, in the one place that decides what it offers.
 *
 * There are two independent reasons this control can be read-only, and keeping
 * them apart matters because they say different things to the owner:
 *
 * `locked` — *this business* can no longer move: it is served on a number in
 * its own country, and a forward number is always national (doc 13a). The
 * owner is NOT told that, though. Since doc 18 no business owns a number —
 * every one of them is served through the shared PL pool line — so "fixed to
 * your +48… number" named a number they had never been given and asked them to
 * open a second business to leave a country we do not operate in anyway. Both
 * disabled states now say the one thing that is true and useful: Poland only,
 * Polish numbers only.
 *
 * Only one country on offer — nothing about this business, just how much of the
 * product exists today. `SELECTABLE_COUNTRIES` is currently `['PL']`, so the
 * picker has a single option and a choice with one outcome is not a choice; it
 * renders disabled rather than as a menu that appears to do something. Widening
 * that list turns the control back into a real picker with no edit here.
 *
 * Legacy FR/DE businesses still render their own country (`countryOptions`) —
 * a picker that showed "Poland" for a business in France would be lying about
 * where its number and its appointment timezone live.
 */
export function CountryField({
  country,
  onCountry,
  savedCountry,
  locked,
}: {
  country: Country;
  onCountry: (v: Country) => void;
  /** The country as STORED, when there is a business to store one — which is
   * not the same as `country`, the live selection. Omitted while creating a
   * business, where nothing is persisted yet. */
  savedCountry?: Country;
  /** Set once a number is held — its country is this business's country. */
  locked?: boolean;
}) {
  // Keyed on what is SAVED, never on the live selection. Deriving it from
  // `country` meant a legacy FR business lost its own option the instant the
  // owner picked Poland: the list collapsed to one entry, the control disabled
  // itself mid-edit, and the wizard's profile step — which has no Discard —
  // stranded them in a country change they had not committed to.
  const { t } = useI18n();
  const options = countryOptions(savedCountry ?? country);
  // One option is not a choice. Derived from what we render rather than from
  // `SELECTABLE_COUNTRIES` directly, so a legacy FR business — which gets a
  // second option — stays free to move to PL.
  const singleOption = options.length < 2;
  const disabled = locked || singleOption;

  return (
    <Field
      label={t('country.label')}
      required
      disabled={disabled}
      hint={
        locked
          ? t('country.lockedReason')
          : singleOption
            ? t('country.singleOption', { country: COUNTRY_LABELS[SELECTABLE_COUNTRIES[0]] })
            : t('country.hint')
      }
    >
      <Select value={country} onChange={(e) => onCountry(e.target.value as Country)}>
        {options.map((c) => (
          <option key={c} value={c}>
            {COUNTRY_LABELS[c]}
          </option>
        ))}
      </Select>
    </Field>
  );
}
